/**
 * De regels van het gezamenlijke kiezen van taken, voor het scherm en de server samen.
 *
 * Niet in het origineel: daar rekende alleen de browser. `modifyTask` stond in
 * client/imports/ui/Tasks/util.ts; de spoedtoeslag en de controle op monteurs stonden in
 * `Task.tsx`. De server nam het bedrag van de browser over. Klikten twee spelers tegelijk op
 * twee kaarten die hetzelfde onderdeel misten, dan betaalde niemand de spoedtoeslag. Ook
 * konden twee kaarten dezelfde monteur krijgen. Nu rekent de server met deze functies en
 * beslist hij; het scherm gebruikt ze voor een snel antwoord.
 */
import { inVolgorde, takenMetSpoed } from '../berekeningen/calculations.js';
import { getSpecificTaskData, getTask } from '../data/taken.js';
import { InGameEffect } from '../types/inGameEffect.js';
import { DieOption } from '../types/randomizers.js';
import { TaskType } from '../types/tasks.js';
import { isOpleiding, opleidingProbleem } from './opleiden.js';
/** Wat een spoedbestelling van een ontbrekend onderdeel kost. */
export const SPOEDBESTELLING = 1000;
/**
 * Waarom een kaart niet gekozen kan worden; de server stuurt dit als `SpelFout`. Een
 * ontbrekende monteur staat hier niet: die tekst noemt het niveau (`geenMonteurMelding`).
 */
export const KEUZE_MELDING = {
    geenKeuze: 'Er loopt nu geen gezamenlijke keuze.',
    nietIngebracht: 'Deze taak hoort niet bij de gezamenlijke keuze.',
    geenMonteurOp4: 'Er is geen monteur op kennisniveau 4 om op te leiden!',
    niemandOpTeLeiden: 'Er is geen monteur meer om op te leiden!',
    geenSlijtage: 'Op deze machine is geen slijtage meer om weg te halen!',
};
/**
 * De taak zoals de spelers hem zien: goedkoper of makkelijker naarmate het OBS en het
 * magazijnbeheer beter zijn. Geeft een nieuwe taak terug; het origineel blijft ongemoeid.
 *
 * Niet in het origineel: bij `levelMechanicNeeded: 0` (de storingen) is het niveau aan het
 * begin van het spel gekozen (`complexityMechanics`). Het origineel toonde en controleerde
 * dat niveau niet. Nu staat het op de kaart en telt OBS-niveau 5 ook hier.
 */
export const modifyTask = (task, game) => {
    const { _id, type, taskEffects, needsMechanic } = task;
    let { cost, levelMechanicNeeded } = task;
    const obsLevel = game.dataManagementSystemData;
    const storageManagement = game.assetData.storageManagement;
    if (needsMechanic && levelMechanicNeeded === 0) {
        levelMechanicNeeded = game.complexityMechanics[_id] ?? 0;
    }
    if (levelMechanicNeeded !== undefined && levelMechanicNeeded > 1 && obsLevel >= 5) {
        levelMechanicNeeded -= 1;
    }
    if (obsLevel >= 6 && [TaskType.onderhoud, TaskType.storing].includes(type))
        cost -= 600;
    if (obsLevel >= 2 && type === TaskType.assetData)
        cost -= 300;
    if (storageManagement >= 5) {
        if (taskEffects?.find((effect) => effect.type === InGameEffect.buySparepart))
            cost /= 2;
        if (storageManagement >= 6) {
            if (type === TaskType.obs)
                cost *= 1.3;
            if (type === TaskType.storing)
                cost *= 0.5;
        }
        else {
            if (type === TaskType.obs)
                cost *= 1.1;
            if (type === TaskType.storing)
                cost *= 0.8;
        }
    }
    cost = Math.round(cost);
    cost = Math.max(cost, 0);
    return { ...task, cost, levelMechanicNeeded };
};
/** De gekozen taken zoals de spelers ze zien, in de volgorde van kiezen. */
const gezien = (game, taakIds) => inVolgorde(getSpecificTaskData(taakIds), taakIds).map((taak) => modifyTask(taak, game));
/**
 * Het aantal spoedbestellingen: per onderdeel de taken die meer vragen dan er open ligt. De
 * verdeling zelf staat in `takenMetSpoed`, zodat de levertijd in de berekening bij dezelfde
 * taken hoort.
 */
const spoedbestellingen = (game, taken) => takenMetSpoed(game, taken).length;
/**
 * Wat deze keuze samen kost: de taken plus een spoedbestelling voor elk onderdeel dat niet
 * open in het magazijn ligt. Hangt niet af van de volgorde van klikken.
 */
export function kostenVanKeuze(game, taakIds) {
    const taken = gezien(game, taakIds);
    const kosten = taken.reduce((som, taak) => som + taak.cost, 0);
    return kosten + spoedbestellingen(game, taken) * SPOEDBESTELLING;
}
/**
 * Of er bij het kiezen van deze taak een spoedbestelling bijkomt: na de taken die al gekozen
 * zijn, krijgt hij geen open onderdeel meer (`takenMetSpoed`).
 */
export function spoedVoor(game, gekozen, taakId) {
    const zonder = gekozen.filter((id) => id !== taakId);
    return takenMetSpoed(game, gezien(game, [...zonder, taakId])).some((taak) => taak._id === taakId);
}
/** De id's van de monteurs die nu gewond zijn; die tellen bij het kiezen niet mee. */
const gewondeMonteurs = (game) => game.modifiers
    .filter((modifier) => modifier.type === InGameEffect.mechanicInjured)
    .flatMap((modifier) => (modifier.mechanic ? [modifier.mechanic] : []));
/**
 * Of elke gekozen taak die een monteur vraagt, een eigen monteur van minstens dat niveau
 * kan krijgen. Gewonde monteurs tellen niet mee. Dat kan precies als er voor elk gevraagd
 * niveau minstens zoveel monteurs op of boven dat niveau zijn als taken die het vragen; de
 * volgorde van kiezen maakt dan niet uit.
 */
export function monteursGenoeg(game, taakIds) {
    const nodig = gezien(game, taakIds)
        .filter((taak) => taak.needsMechanic)
        .map((taak) => taak.levelMechanicNeeded ?? 0);
    const gewond = gewondeMonteurs(game);
    const niveaus = game.mechanics
        .filter((monteur) => !gewond.includes(monteur._id))
        .map((monteur) => monteur.knowledgeLevel);
    return nodig.every((niveau) => nodig.filter((n) => n >= niveau).length <= niveaus.filter((n) => n >= niveau).length);
}
/** Het begin van elke tekst van `geenMonteurMelding`. */
const MONTEUR_VRAAG = 'Deze taak vraagt een monteur';
/** Of een tekst van de server een ontbrekende monteur meldt (`geenMonteurMelding`). */
export const isGeenMonteurMelding = (tekst) => tekst.startsWith(MONTEUR_VRAAG);
/**
 * Niet in het origineel: daar stond altijd "Er is geen monteur met het juiste kennisniveau
 * beschikbaar!". Nu noemt de tekst het niveau van de kaart (na `modifyTask`, zoals op de
 * kaart) en waarom er geen monteur is. Alleen de reden, geen advies:
 * - er is geen monteur op dat niveau of hoger;
 * - die er zijn, zijn allemaal gewond (een `mechanicInjured`-modifier);
 * - anders vragen de andere gekozen taken ze al. Zonder andere taken met een monteur kan dat
 *   niet: dan was er een gezonde monteur voor deze taak. Ook als het tekort bij een lager
 *   niveau zit (twee taken op niveau 1, monteurs op 1 en 5, een derde taak op 5) zijn de
 *   monteurs op of boven dit niveau nodig voor de andere taken.
 */
export function geenMonteurMelding(game, taak) {
    const niveau = modifyTask(taak, game).levelMechanicNeeded ?? 0;
    const vraag = niveau <= 0
        ? `${MONTEUR_VRAAG}.`
        : niveau >= 5
            ? `${MONTEUR_VRAAG} van niveau 5.`
            : `${MONTEUR_VRAAG} van niveau ${niveau} of hoger.`;
    const gewond = gewondeMonteurs(game);
    const passend = game.mechanics.filter((monteur) => monteur.knowledgeLevel >= niveau);
    const gewondPassend = passend.filter((monteur) => gewond.includes(monteur._id)).length;
    const gezond = passend.length - gewondPassend;
    if (passend.length === 0)
        return `${vraag} Die is er niet.`;
    if (gezond === 0)
        return `${vraag} ${gewondPassend === 1 ? 'Die is' : 'Die zijn'} gewond.`;
    if (gewondPassend > 0)
        return `${vraag} Die zijn gewond of deze week al ingezet.`;
    return `${vraag} ${gezond === 1 ? 'Die is' : 'Die zijn'} deze week al ingezet.`;
}
/**
 * Hoeveel slijtage (gele kanten op de dobbelsteen) een machine nog heeft voor nieuw
 * onderhoud. Elke gekozen onderhoudstaak op die machine maakt er één groen.
 */
export function slijtageOver(game, gekozenMachines, asset) {
    const geel = (game.dashboard.dice[asset]?.die ?? []).filter((kant) => kant === DieOption.yellow).length;
    const alGekozen = gekozenMachines.filter((keuze) => keuze.asset === asset && getTask(keuze.id)?.type === TaskType.onderhoud).length;
    return geel - alGekozen;
}
/**
 * Waarom deze taak niet bij de gekozen taken kan (een tekst uit `KEUZE_MELDING` of
 * `geenMonteurMelding`), of `undefined` als het kan: een monteur van het goede niveau, een monteur om op te leiden en
 * bij onderhoud slijtage op de gekozen machine.
 */
export function keuzeFout(game, gekozen, gekozenMachines, taakId, asset) {
    const taak = getTask(taakId);
    if (!taak)
        return KEUZE_MELDING.nietIngebracht;
    const nieuw = [...gekozen.filter((id) => id !== taakId), taakId];
    if (taak.needsMechanic && !monteursGenoeg(game, nieuw))
        return geenMonteurMelding(game, taak);
    if (isOpleiding(taak)) {
        const probleem = opleidingProbleem(game.mechanics, nieuw);
        if (probleem)
            return KEUZE_MELDING[probleem];
    }
    if (taak.type === TaskType.onderhoud &&
        asset &&
        slijtageOver(game, gekozenMachines, asset) <= 0) {
        return KEUZE_MELDING.geenSlijtage;
    }
    return undefined;
}
/**
 * Of het gezamenlijke kiezen loopt: er is iets ingebracht en iedere speler met een naam
 * heeft zijn taken ingebracht.
 *
 * Niet in het origineel: daar moest elke plek inleggen, ook een plek waar niemand was
 * ingelogd. Een spel voor vier dat met drie gespeeld werd, kwam zo nooit bij het kiezen.
 */
export const gedeeldeKeuzeLoopt = (spelers) => spelers.some((speler) => speler.sharedTasks.length > 0) &&
    spelers.every((speler) => !speler.name || speler.sharedTasks.length > 0);
/** Of een plek meedoet: er is iemand met een naam ingelogd, of er zijn al taken ingebracht. */
export const doetMee = (speler) => !!speler.name || speler.sharedTasks.length > 0;
